import { useContext, useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'

import { Typography, TextareaAutosize, Stepper, Step, StepLabel, StepContent, Box, Button, Tooltip, IconButton, InputAdornment, TextField, RadioGroup, Radio, FormControlLabel } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { CopyToClipboard } from 'react-copy-to-clipboard'
import { enqueueSnackbar } from 'notistack'
import QRCode from "react-qr-code"
import BwipJs from "@bwip-js/browser"
import moment from 'moment-timezone'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { AppContext } from '/providers/app-context'
import { createTestStampcard, createTestLoyaltycard, createGiftcard } from '/providers/studio-api'

import PageWrapper from '/components/page-wrapper'
import StyledPaper from '/components/paper'
import Menu from '/components/templates/template-nav-menu'
import batchSendMakePasses from '/components/make-passes/batch-send-make-passes'
import getSteps from '/components/make-passes/steps'
import parseUploadedData from '/components/make-passes/parse-uploaded-data'
import ResultsGrid from '/components/make-passes/results-grid'
import DataExportDialog from '/components/data-export-dialog'
import excelExport from '/components/make-passes/sample-data-to-excel'
import csvExport from '/components/make-passes/sample-data-to-csv'
import ActivePassLimitErrorDialog from '/components/make-passes/active-pass-limit-error-dialog'

import HelpText from '/help/template-issue-passes'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'

const IssuePasses = () => {

  const navigate = useNavigate()
  const { loadingTemplates, templates } = useContext(AppContext)
  const { templateId } = useParams()

  const template = useMemo(() => { return templates.filter(t => t.id === templateId)[0] }, [templates, templateId])

  useEffect(() => {
    if (!loadingTemplates && !template) navigate("/templates")
  }, [template])

  if (template?.pass_type_id === "smartix_stamp") return (
    <PageWrapper title="Issue New Stampcard" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <StampcardSignup template={template} />
      </div>
    </PageWrapper>
  )

  if (template?.pass_type_id === "smartix_loyalty") return (
    <PageWrapper title="Issue New Loyalty card" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <LoyaltycardSignup template={template} HelpText={HelpText} />
      </div>
    </PageWrapper>
  )

  if (template?.pass_type_id === "smartix_gift") return (
    <PageWrapper title="Issue New Giftcard" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <Giftcard template={template} HelpText={HelpText} />
      </div>
    </PageWrapper>
  )

  return (
    <PageWrapper title="Issue New Passes" HelpText={HelpText} heading={`${template?.name} ${template?.archived ? " [archived]" : ""}`} menu={<Menu template={template} />}>
      <div className='overflow-y-scroll h-full'>
        <Content template={template} />
      </div>
    </PageWrapper>
  )
}

export default IssuePasses


const Content = ({ template }) => {

  const { apiSource, apiKey } = useContext(AppContext)

  const [activeStep, setActiveStep] = useState(0)
  const [passData, setPassData] = useState()
  const [apiMode, setApiMode] = useState("test")
  const [dataSource, setDataSource] = useState("sample")
  const [processing, setProcessing] = useState(false)
  const [passesToProcess, setPassesToProcess] = useState(0)
  const [makePassResults, setMakePassResults] = useState([])
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})
  const [instanceData, setInstanceData]= useState({})

  useEffect(() => {
    setApiMode("test")
  }, [])

  useEffect(() => {
    if (apiMode === "live") setDataSource("upload")
    else setDataSource("sample")
  }, [apiMode])

  useEffect(() => {
    if (template) {
      setInstanceData(template.instance_data ?? {})
    }
  },[template])

  if (!template) return null

  const handleStepChange = num => setActiveStep(activeStep + num)

  const handleDataFileUpload = async file => {
    const reader = new FileReader()
    reader.onloadend = () => {

      const base64Data = reader.result.replace(/[^,]+base64,/, '')
      const parsedData = parseUploadedData({ base64Data, file, instance_data: template.instance_data })

      setPassData(parsedData)
    }
    reader.readAsDataURL(file)
  }


  const makePasses = async () => {
    setProcessing(true)

    const response = await batchSendMakePasses({ apiKey, apiSource, apiMode, dataSource, passData, templateId: template.id, instanceData, setPassesToProcess, setMakePassResults })

    setApiMode("test")
    setDataSource("sample")
    setActiveStep(0)
    setPassData()

    if (!response.ok) {
      setMakePassError(response.error)
      setErrorDialogOpen(true)
    }
  }

  const steps = getSteps({ apiMode, setApiMode, handleStepChange, dataSource, setDataSource, handleDataFileUpload, passData, makePasses })

  if (processing) return (
    <div className='h-full'>
      <>
        <ResultsGrid template={template} passesToProcess={passesToProcess} makePassResults={makePassResults} setMakePassResults={setMakePassResults} setProcessing={setProcessing} />
        <ActivePassLimitErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
      </>
    </div>
  )

  return (
    <>
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${template.id}-sample-data`} data={[template.instance_data]} excelExport={excelExport} csvExport={csvExport} />
      <div className='flex flex-wrap gap-5 h-full w-full mt-5'>

        <div className='overflow-y-scroll ml-3'>
          <div className='w-[600px] max-w-[800px] min-w-[600px]'>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      index === steps.length - 1 ? (
                        <Typography variant="caption">Last step</Typography>
                      ) : null
                    }
                  >
                    {step.label}
                  </StepLabel>

                  <StepContent>
                    <Box sx={{ mb: 2, p:3 }}>
                      <StyledPaper>
                        {step.content}
                      </StyledPaper>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            <p>Please check your imported data against the sample data for this template.</p>
          </div>
        </div>

        <div className="flex flex-wrap flex-1">
          <div className='min-w-[400px] flex-1'>
            <div className='ml-3'>
              <Typography variant='h4'>Sample Data <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip></Typography>
            </div>
            <StyledPaper>
              <TextareaAutosize
                maxRows={100}
                minRows={30}
                value={JSON.stringify(instanceData, null, 2)}
                className='w-full p-5 bg-transparent'
                contentEditable={false}
              /></StyledPaper>
          </div>

          {dataSource === "upload" && <>
            <div className='min-w-[400px] flex-1'>
              <div className='ml-3'>

                <Typography variant='h4'>Imported Data</Typography>
              </div>
              <StyledPaper>
                <TextareaAutosize
                  maxRows={100}
                  minRows={30}
                  value={JSON.stringify(passData, null, 2)}
                  className='w-full p-5 bg-transparent'
                  contentEditable={false}
                />
              </StyledPaper>
            </div>
          </>}
        </div>


      </div >
    </>
  )
}


const barcodeOptions = {
  bcid: 'qrcode',
  scale: 2,
  includetext: false,
  eclevel: "M",
  ecaddchars: 3,
  layers: 8,
  width: 60,
  height: 60,
  padding: 8,
  backgroundcolor: "#ffffff"
}


const StampcardSignup = ({ template }) => {

  const { apiSource, apiKey, setProcessing, client } = useContext(AppContext)

  const [stampCard, setStampCard] = useState()
  const [barcode, setBarcode] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/stamp/${client?.id}/${template.id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)
  }, [])

  const makeTestStampcard = async () => {
    setStampCard(null)
    setProcessing(true)
    const sc = await createTestStampcard({ apiSource, apiKey, apiMode: "test", templateId: template.id })
    setProcessing(false)

    if (sc.ok === false) {
      setMakePassError(sc.error)
      setErrorDialogOpen(true)
    } else {
      setStampCard(sc)
    }
  }

  const gotoSignupForm = () => {
    window.open(`https://signup.smartix.uk/${template.client_id}/${template.id}`)
  }

  return (
    <>
      <div className='m-10 flex flex-col gap-10'>
        <div className='m-10 flex flex-row gap-10 justify-around'>

          <StyledPaper>
            <div className='flex flex-col gap-10 p-5 items-center'>
              {!stampCard &&
                <>
                  <div className='mt-40'>You can create a test stampcard here.</div>
                  <div><Button variant="contained" onClick={makeTestStampcard}>Create a TEST Stampcard</Button></div>
                </>
              }

              {stampCard &&
                <>
                  <Typography variant='h4'>
                    <CopyToClipboard text={stampCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })}>
                      <div className='text-nowrap  cursor-pointer'><ContentCopyIcon color='inherit' className="mr-5" />New Stampcard ID: {stampCard.pass_id}</div>
                    </CopyToClipboard>
                  </Typography>
                  <div className='flex gap-40 items-center m-10'>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={stampCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={stampCard.appleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={stampCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={stampCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={stampCard.googleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={stampCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                  </div>
                  <div className='flex flex-row justify-end w-full'>
                    <Button variant='outlined' onClick={() => setStampCard(null)}>close</Button>
                  </div>
                </>
              }
            </div>
          </StyledPaper>

          {!stampCard &&
            <StyledPaper>
              <div className='flex flex-col gap-3 items-center p-5'>
                <div>If you wish to create live stampcard, then you must use the stampcard signup form </div>
                <Button variant="contained" onClick={gotoSignupForm}>Go to live signup form</Button>

                <img src={barcode} className="mt-5" />
                <div>Scan for link to signup form</div>
                <div><a target="_blank" href={`https://signup.smartix.uk/${template.client_id}/${template.id}`}>https://signup.smartix.uk/{template.client_id}/{template.id}</a></div>
              </div>
            </StyledPaper>
          }
        </div>
      </div>

      <ActivePassLimitErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />

    </>
  )
}


const LoyaltycardSignup = ({ template }) => {

  const { apiSource, apiKey, setProcessing, client } = useContext(AppContext)

  const [loyaltyCard, setLoyaltyCard] = useState()
  const [barcode, setBarcode] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/loyalty/${client?.id}/${template.id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)
  }, [])

  const makeTestLoyaltycard = async () => {
    setLoyaltyCard(null)
    setProcessing(true)
    const sc = await createTestLoyaltycard({ apiSource, apiKey, apiMode: "test", templateId: template.id })
    setProcessing(false)

    if (sc.ok === false) {
      setMakePassError(sc.error)
      setErrorDialogOpen(true)
    } else {
      setLoyaltyCard(sc)
    }
  }

  const gotoSignupForm = () => {
    window.open(`https://signup.smartix.uk/loyalty/${template.client_id}/${template.id}`)
  }

  return (
    <>
      <div className='m-10 flex flex-col gap-10'>
        <div className='m-10 flex flex-row gap-10 justify-around'>

          <StyledPaper>
            <div className='flex flex-col gap-10 p-5 items-center'>
              {!loyaltyCard &&
                <>
                  <div className='mt-40'>You can create a test loyalty card here.</div>
                  <div><Button variant="contained" onClick={makeTestLoyaltycard}>Create a TEST Loyalty card</Button></div>
                </>
              }

              {loyaltyCard &&
                <>
                  <Typography variant='h4'>
                    <CopyToClipboard text={loyaltyCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })}>
                      <div className='text-nowrap  cursor-pointer'><ContentCopyIcon color='inherit' className="mr-5" />New Loyalty card ID: {loyaltyCard.pass_id}</div>
                    </CopyToClipboard>
                  </Typography>
                  <div className='flex gap-40 items-center m-10'>


                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={loyaltyCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={loyaltyCard.appleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={loyaltyCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={loyaltyCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={loyaltyCard.googleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={loyaltyCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>
                  </div>
                  <div className='flex flex-row justify-end w-full'>
                    <Button variant='outlined' onClick={() => setLoyaltyCard(null)}>close</Button>
                  </div>
                </>
              }
            </div>
          </StyledPaper>

          {!loyaltyCard &&
            <StyledPaper>
              <div className='flex flex-col gap-3 items-center p-5'>
                <div>If you wish to create live loyalty card, then please use the loyalty card signup form: </div>
                <Button variant="contained" onClick={gotoSignupForm}>Go to live signup form</Button>

                <img src={barcode} className="mt-5" />
                <div>Scan for link to signup form</div>
                <div><a target="_blank" href={`https://signup.smartix.uk/loyalty/${template.client_id}/${template.id}`}>https://signup.smartix.uk/{template.client_id}/{template.id}</a></div>
              </div>
            </StyledPaper>
          }
        </div>
      </div>

      <ActivePassLimitErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />

    </>
  )
}



const Giftcard = ({ template }) => {

  const { apiSource, apiKey, setProcessing,copyingEmail, setCopyingEmail } = useContext(AppContext)

  const [giftCard, setGiftCard] = useState(null)
  const [emailBarcodes, setEmailBarcodes] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  const navigate = useNavigate()

  const copyEmail = () => {
    setCopyingEmail(true)
  }

  const copyEmailBarcodes = () => {
    setEmailBarcodes(true)
    setCopyingEmail(true)
  }

  useEffect(() => {
    if (copyingEmail) {
      document.execCommand("SelectAll", true)
      document.execCommand("Copy", true)
      document.execCommand("UnSelect", true)

      setCopyingEmail(false)
      setEmailBarcodes(false)
    }
  }, [copyingEmail])

  const makeTestGiftcard = async () => {
    setGiftCard(null)
    setProcessing(true)
    const gc = await createGiftcard({ apiSource, apiKey, apiMode: "test", templateId: template.id })
    setProcessing(false)

    if (gc.ok === false) {
      setMakePassError(gc.error)
      setErrorDialogOpen(true)
    } else {
      setGiftCard(gc)
    }
  }

  if (copyingEmail) return (
    <div style={{ backgroundColor: "#fff", width: "100%" }}>
      <br />
      <table cellPadding={20} cellSpacing={20}>
        <tbody>
          <tr style={{ height: 100 }}>
            <td><Link to={giftCard?.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link></td>
            <td><Link to={giftCard?.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link></td>
          </tr>
          {emailBarcodes &&
            <tr style={{ height: 200, width: 200 }}>
              <td >
                <div className='bg-white p-5'>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={giftCard?.appleUrl}
                    viewBox={`0 0 256 256`}
                  />
                </div>
              </td>

              <td >
                <div className='bg-white p-5'>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={giftCard?.googleUrl}
                    viewBox={`0 0 256 256`} />
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
      <br /><br />
    </div>
  )

  return (
    <>
      <div className='m-10 flex flex-col gap-10 h-full overflow-y-auto'>
        {!giftCard &&
          <div className='m-10 flex flex-row gap-10 justify-around'>

            <StyledPaper>
              <div className='flex flex-col gap-3 p-5'>
                <div className='mt-28'>You can create a test giftcard here.</div>
                <Button variant="contained" onClick={makeTestGiftcard}>Create a TEST Giftcard</Button>
              </div>
            </StyledPaper>

            <StyledPaper>
              <div className='flex flex-col gap-3 p-5'>
                <div className='flex flex-col gap-3 items-start'><Typography variant="h5" sx={{ p: 2 }}>Create a Giftcard</Typography></div>
                <CreateLiveGiftcardForm template={template} setGiftCard={setGiftCard} />
              </div>
            </StyledPaper>

          </div>
        }

        {giftCard &&
          <div className='flex flex-row gap-40 items-center m-10'>

            <StyledPaper>
              <div className='flex flex-row gap-20 p-5'>
                <div className='flex flex-col gap-10 items-start'>
                  <div style={{ height: 100 }} className="flex flex-col justify-around">
                    <Link to={giftCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                  </div>

                  <div style={{ height: 250, width: 250 }} className='bg-white p-5' >
                    <QRCode
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={giftCard.appleUrl}
                      viewBox={`0 0 256 256`}
                    />
                  </div>

                  <CopyToClipboard text={giftCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                    <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
                  </CopyToClipboard>
                </div>

                <div className='flex flex-col gap-10 items-start'>
                  <div style={{ height: 100 }} className="flex flex-col justify-around">
                    <Link to={giftCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                  </div>

                  <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                    <QRCode
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={giftCard.googleUrl}
                      viewBox={`0 0 256 256`}
                    />
                  </div>

                  <CopyToClipboard text={giftCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                    <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
                  </CopyToClipboard>
                </div>
              </div>
            </StyledPaper>

            <StyledPaper>
              <div className='flex flex-col gap-5 p-5'>
                <Typography variant='h6'>Gift card ID:
                  <CopyToClipboard text={giftCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Gift card ID copied', variant: 'success' })} options={{ format: 'text/plain' }}>
                    <Button ><ContentCopyIcon color='inherit' className="mr-5" />{giftCard.pass_id}</Button>
                  </CopyToClipboard>
                </Typography>

                <Button variant='contained' color="secondary" onClick={copyEmailBarcodes}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (with barcodes)</Button>
                <Button variant='contained' color="secondary" onClick={copyEmail}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (no barcodes)</Button>

                <Button variant='contained' color="secondary" onClick={() => navigate("../passes/" + giftCard.pass_id)}>Goto pass details</Button>
                <br />
                <Button variant='contained' onClick={() => setGiftCard(null)}>Close</Button>
              </div>
            </StyledPaper>

          </div>
        }

      </div>

      <ActivePassLimitErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}


const CreateLiveGiftcardForm = ({ template, setGiftCard }) => {
  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const defaultExpiry = moment().add(1, "year")

  const [amount, setAmount] = useState("")
  const [hasExpiry, setHasExpiry] = useState("yes")
  const [expiry, setExpiry] = useState(defaultExpiry)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState()

  const regex = /^(\d+(?:[\.]\d{0,2})?)$/i

  const makeGiftcard = async () => {
    setGiftCard(null)
    setProcessing(true)

    const gc = await createGiftcard({ apiSource, apiKey, apiMode: "live", templateId: template.id, amount: parseInt(amount, 10), expiry: hasExpiry === "yes" ? expiry.format("YYYYMMDDHHmmSS") : undefined })

    setProcessing(false)

    if (gc.ok === false) {
      setMakePassError(gc.error)
      setErrorDialogOpen(true)
    } else {
      setGiftCard(gc)
    }
  }

  return (
    <>
      <div>

        <TextField
          sx={{ m: 1, width: '25ch' }}
          value={amount}
          onChange={e => {
            if (e.target.value === '' || regex.test(e.target.value)) {
              setAmount(e.target.value)
            }
          }}
          variant='outlined'
          label="Amount"
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><div style={{ fontSize: 40, color: "#999" }}>£</div></InputAdornment>,
              style: { fontSize: 40, width: 235 }
            },
            inputLabel: {
              shrink: true
            }
          }}
        />

      </div>
      <RadioGroup
        row
        value={hasExpiry}
        onChange={e => setHasExpiry(e.target.value)}
      >
        <FormControlLabel value="yes" control={<Radio />} label="Set an expiry date" />
        <FormControlLabel value="no" control={<Radio />} label="No expiry date" />
      </RadioGroup>


      <div><DatePicker label="Expiry Date" value={expiry} onChange={date => setExpiry(date)} disabled={hasExpiry === "no"} /></div>

      <div><Button onClick={makeGiftcard} variant='contained' disabled={amount < 1} >Create Giftcard</Button></div>

      <ActivePassLimitErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}
